import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Heart } from "lucide-react";

const STEPS = [
  { k: "01", title: "Patient walks in.", body: "Self-check-in, ABHA pull, smart triage. The OS already knows their journey." },
  { k: "02", title: "Floor moves as one.", body: "OPD, ward, pharmacy and billing share a single live state — no more parallel registers." },
  { k: "03", title: "Decisions get faster.", body: "Real-time vitals, queue depth and bed maps surface to whoever needs them, exactly when." },
  { k: "04", title: "Discharge is one click.", body: "Auto-collected dues, signed summary, follow-up scheduled — patient leaves smiling, not waiting." },
];

const STAT_OVERLAYS = [
  { label: "Avg. patient wait", value: "6m 14s" },
  { label: "ICU occupancy", value: "78%" },
  { label: "Pending discharges", value: "3" },
  { label: "Today's revenue", value: "₹12.4L" },
];

const ORBIT_LABELS = ["OPD", "IPD", "Pharma", "Billing", "Wards", "Visitor"];

function StepCard({ step, idx, total, scrollYProgress }) {
  const clamp = (v) => Math.max(0, Math.min(1, v));
  const start = idx / total;
  const end = (idx + 1) / total;
  const a = clamp(start - 0.05);
  const b = clamp(start + 0.05);
  const c = clamp(end - 0.05);
  const d = clamp(end + 0.05);
  // ensure strictly non-decreasing & inside [0,1]
  const stops = [a, Math.max(a, b), Math.max(a, b, c), Math.max(a, b, c, d)];
  const opacity = useTransform(scrollYProgress, stops, [0.25, 1, 1, 0.25]);
  return (
    <motion.li
      style={{ opacity }}
      className="bg-white border border-ab-border rounded-3xl p-6 md:p-8"
      data-testid={`os-step-${step.k}`}
    >
      <div className="flex items-start gap-5">
        <span className="font-mono-tech text-ab-accent text-xs tracking-widest mt-2">{step.k}</span>
        <div>
          <h3 className="font-serif text-3xl text-ab-primary leading-tight">{step.title}</h3>
          <p className="mt-2 text-ab-muted leading-relaxed">{step.body}</p>
        </div>
      </div>
    </motion.li>
  );
}

export default function OSUnifier() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  const breath = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.06, 1]);
  const ringRotate = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const ringRotateRev = useTransform(scrollYProgress, [0, 1], [0, -360]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0, -30]);

  return (
    <section ref={ref} id="os" data-testid="os-unifier-section" className="relative bg-ab-bg">
      <div className="ab-container pt-24 md:pt-32 pb-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="ab-eyebrow text-ab-accent">The Unifier OS</p>
            <h2 className="ab-h2 text-5xl md:text-6xl mt-4 max-w-2xl">
              One system. <span className="italic font-light">Every</span> department, breathing together.
            </h2>
          </div>
          <p className="text-ab-muted max-w-md">
            Most "HMS" tools are stitched modules pretending to be a system. PranaOs is one OS — every screen, every role, on the same heartbeat.
          </p>
        </div>
      </div>

      <div className="relative" style={{ height: `${STEPS.length * 90}vh` }}>
        <div className="sticky top-0 h-screen flex items-center overflow-hidden">
          <div className="ab-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full">
            <div className="lg:col-span-6 relative h-[480px] flex items-center justify-center">
              <motion.div style={{ rotate: ringRotate }} className="absolute w-[420px] h-[420px] rounded-full border border-ab-border" />
              <motion.div style={{ rotate: ringRotateRev }} className="absolute w-[320px] h-[320px] rounded-full border border-ab-primary/15 border-dashed" />
              <motion.div
                style={{ scale: breath }}
                className="relative w-56 h-56 rounded-full bg-ab-primary text-white flex items-center justify-center shadow-[0_30px_80px_-20px_rgba(13,64,53,0.5)]"
              >
                <Heart className="w-14 h-14" strokeWidth={1.2} />
                <span className="absolute inset-0 rounded-full ring-2 ring-ab-accent/40 animate-ping" />
              </motion.div>

              {ORBIT_LABELS.map((t, i) => {
                const angle = (i / ORBIT_LABELS.length) * Math.PI * 2;
                const r = 200;
                const x = Math.cos(angle) * r;
                const y = Math.sin(angle) * r;
                return (
                  <motion.div
                    key={t}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * i }}
                    className="absolute bg-white border border-ab-border rounded-full px-4 py-2 text-xs font-medium text-ab-primary shadow-md"
                    style={{ transform: `translate(${x}px, ${y}px)` }}
                  >
                    {t}
                  </motion.div>
                );
              })}

              <motion.div style={{ y: overlay }} className="absolute -bottom-6 left-0 right-0 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-[420px] mx-auto">
                {STAT_OVERLAYS.map((s, i) => (
                  <div key={i} className="bg-white border border-ab-border rounded-xl px-3 py-2 text-center">
                    <p className="text-[10px] tracking-widest uppercase text-ab-muted">{s.label}</p>
                    <p className="font-serif text-xl text-ab-primary mt-0.5">{s.value}</p>
                  </div>
                ))}
              </motion.div>
            </div>

            <div className="lg:col-span-6 relative">
              <ol className="space-y-5">
                {STEPS.map((step, idx) => (
                  <StepCard key={step.k} step={step} idx={idx} total={STEPS.length} scrollYProgress={scrollYProgress} />
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
